<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { formatDate } from '@/utils/dateUtils';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useWorksheetComposable } from './useWorksheetComposable';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import QuoteInfo from './QuoteInfo.vue';
import TourOptions from './TourOptions.vue';
import ViewPDFFile from '@/components/modal/utils/ViewPDFFile.vue';

const dialogRef = inject('dialogRef') as Ref;
const reloadList = dialogRef.value.options.emits.reloadList;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const adminQuoteStore = useAdminQuoteStore();
const tourWorkSheet = computed(() => {
  return adminQuoteStore.currentQuote;
});

const { handleConvertBrTag } = useWorksheetComposable();

const submitWorksheetStore = useSubmitWorksheetStore();
const tourWorkSheetRef = ref<any>(tourWorkSheet.value);
const isShowPreviewPDF = authorizations.worksheet_tour_other_section.preview_pdf_view;
const disablePreviewPDF = ref(true);

const handleSaveDataEdit = (event: any) => {
  tourWorkSheetRef.value.multiDayTour = event;
};
const handleSaveDataEditTerm = (event: any) => {
  tourWorkSheetRef.value[event.field] = event.value;
};

const changeDate = (event: any) => {
  tourWorkSheetRef.value[event?.type] = event?.value;
};

const changeLinkedQuotesTour = (event: any) => {
  tourWorkSheetRef.value.linkedQuotes = event?.value;
};

const renderFlightNo = (operated: any, flightNumber: any) => {
  return operated && flightNumber ? `${operated}${flightNumber}` : '';
};

const formatDateFlight = (dayTour: any) => {
  dayTour.tours.forEach((mdt: any) => {
    mdt.flights?.forEach((fly: any) => {
      fly.flightNo = renderFlightNo(fly?.operated, fly?.flightNumber);

      ['arrDate', 'depDate']?.forEach((key) => {
        fly[key] = formatDate(fly[key], 'yyyy-LL-dd HH:mm').replace(' ', 'T');
        delete fly.index;
      });
    });
  });
};

const handleSave = () => {
  const submitData = cloneDeep(tourWorkSheetRef.value.multiDayTour);
  const mdtTemp: any = [];
  submitData.forEach((dayTour: any) => {
    formatDateFlight(dayTour);
    const mdtForm = {
      ...dayTour
    };
    mdtTemp.push(mdtForm);
  });
  const payload = {
    qType: tourWorkSheetRef.value.qType,
    bookedDate: new Date(formatDate(tourWorkSheetRef.value?.bookedDate)),
    expires: new Date(formatDate(tourWorkSheetRef.value?.expires)),
    intro: handleConvertBrTag(tourWorkSheetRef.value.intro),
    termConditions: handleConvertBrTag(tourWorkSheetRef.value.termConditions),
    multiDayTour: mdtTemp,
    linkedQuotes: tourWorkSheetRef.value.linkedQuotes
  };
  submitWorksheetStore
    .handleUpdateWorkSheet(payload, tourWorkSheet.value.sk)
    .then(() => {
      disablePreviewPDF.value = false;
      reloadList();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};
</script>
<template>
  <div class="tour-worksheet">
    <QuoteInfo :tourQuote="tourWorkSheet" @changeDate="changeDate($event)" @changeLinkedQuote="changeLinkedQuotesTour($event)" />
    <TourOptions
      :multiDayTour="tourWorkSheet.multiDayTour"
      :langQuote="tourWorkSheet.lang"
      :quoteId="tourWorkSheet.sk"
      :tourWorkSheet="tourWorkSheetRef"
      @handleSaveDataEdit="handleSaveDataEdit($event)"
      @handleSaveDataEditTerm="handleSaveDataEditTerm($event)"
      @saveWorksheet="handleSave()"
    />
    <div :class="['flex justify-content-center gap-3', 'm-auto']">
      <Button v-if="authorizations.worksheet_tour_other_section.save_view" class="button-save-class action-button" icon="pi pi-check" :label="$t('promos.save')" @click="handleSave()" />
      <ViewPDFFile :quoteID="tourWorkSheet.sk" :disabled="disablePreviewPDF" :isShowBtn="isShowPreviewPDF"></ViewPDFFile>
    </div>
  </div>
</template>
